import React, { useState, useEffect } from 'react';

/**
 * CodeBlock Component
 *
 * Displays code snippets with syntax highlighting and copy-to-clipboard functionality.
 * Responsive design: Adapts layout for mobile devices.
 *
 * Props:
 * - code: The code content to display
 * - language: Optional language label (e.g., "JavaScript", "Python")
 */

const CodeBlock = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div className="relative my-2 rounded-lg overflow-hidden border-2" style={{ borderColor: '#749555' }}>
      {/* Language Label (if provided) */}
      {language && (
        <div
          className={`${isMobile ? 'px-2 py-1.5' : 'px-4 py-2'} text-xs font-semibold text-white border-b-2`}
          style={{
            backgroundColor: '#1a1a1a',
            borderColor: '#749555'
          }}
        >
          {language}
        </div>
      )}

      {/* Code Content */}
      <div className="relative">
        <pre
          className={`${isMobile ? 'p-2' : 'p-4'} text-sm leading-relaxed min-h-[50px]`}
          style={{
            backgroundColor: '#1a1a1a',
            color: '#e5e5e5',
            margin: 0,
            paddingRight: isMobile ? '40px' : '60px', // Extra padding to prevent overlap with button
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          <code className="font-mono">{code}</code>
        </pre>

        {/* Copy Button */}
        <button
          onClick={handleCopy}
          className={`absolute top-2 right-2 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-1.5'} rounded-md font-medium text-sm transition-all flex items-center ${isMobile ? '' : 'gap-2'} shadow-md`}
          style={{
            backgroundColor: copied ? '#749555' : 'rgba(116, 149, 85, 0.2)',
            color: copied ? 'white' : '#749555',
            border: copied ? 'none' : '1px solid #749555'
          }}
          title={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? (
            <>
              <i className="fas fa-check"></i>
              {!isMobile && <span>Copied!</span>}
            </>
          ) : (
            <>
              <i className="far fa-copy"></i>
              {!isMobile && <span>Copy</span>}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CodeBlock;
